import { useEffect, useState } from "react";
import { createPaymentAccount, updatePaymentAccount } from "../../api/paymentAccounts";
import {
  PaymentAccountDTO,
  PaymentAccountType,
  PaymentAccountTypeLabels,
  CreatePaymentAccountDTO,
  UpdatePaymentAccountDTO,
} from "../../models/IPaymentAccount";
import { Alert, Button, Dialog, Field, FormGrid, Input, Select, Switch, Textarea } from "../../ui";
import "./styles.scss";

type PaymentAccountDialogProps = {
  account?: PaymentAccountDTO;
  isOpen: boolean;
  onClose: (update?: boolean) => void;
};

const PaymentAccountDialog = (props: PaymentAccountDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    accountName: "",
    accountType: PaymentAccountType.Instapay,
    paymentAddress: "",
    accountNumber: "",
    iban: "",
    bankName: "",
    currency: "",
    bic: "",
    routingNumber: "",
    paymentUrl: "",
    additionalInfo: "",
    isActive: true,
    displayOrder: 0,
  });

  useEffect(() => {
    if (props.isOpen) {
      if (props.account) {
        setFormData({
          accountName: props.account.accountName,
          accountType: props.account.accountType,
          paymentAddress: props.account.paymentAddress || "",
          accountNumber: props.account.accountNumber || "",
          iban: props.account.iban || "",
          bankName: props.account.bankName || "",
          currency: props.account.currency || "",
          bic: props.account.bic || "",
          routingNumber: props.account.routingNumber || "",
          paymentUrl: props.account.paymentUrl || "",
          additionalInfo: props.account.additionalInfo || "",
          isActive: props.account.isActive,
          displayOrder: props.account.displayOrder,
        });
      } else {
        setFormData({
          accountName: "",
          accountType: PaymentAccountType.Instapay,
          paymentAddress: "",
          accountNumber: "",
          iban: "",
          bankName: "",
          currency: "",
          bic: "",
          routingNumber: "",
          paymentUrl: "",
          additionalInfo: "",
          isActive: true,
          displayOrder: 0,
        });
      }
    }
  }, [props.isOpen, props.account]);

  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.accountName.trim()) {
      setErrorMessage("Account name is required");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      if (props.account) {
        // Update existing account
        const updateData: UpdatePaymentAccountDTO = {
          accountName: formData.accountName,
          accountType: formData.accountType,
          paymentAddress: formData.paymentAddress || undefined,
          accountNumber: formData.accountNumber || undefined,
          iban: formData.iban || undefined,
          bankName: formData.bankName || undefined,
          currency: formData.currency || undefined,
          bic: formData.bic || undefined,
          routingNumber: formData.routingNumber || undefined,
          paymentUrl: formData.paymentUrl || undefined,
          additionalInfo: formData.additionalInfo || undefined,
          isActive: formData.isActive,
          displayOrder: formData.displayOrder,
        };
        await updatePaymentAccount(props.account.id, updateData);
      } else {
        // Create new account
        const createData: CreatePaymentAccountDTO = {
          accountName: formData.accountName,
          accountType: formData.accountType,
          paymentAddress: formData.paymentAddress || undefined,
          accountNumber: formData.accountNumber || undefined,
          iban: formData.iban || undefined,
          bankName: formData.bankName || undefined,
          currency: formData.currency || undefined,
          bic: formData.bic || undefined,
          routingNumber: formData.routingNumber || undefined,
          paymentUrl: formData.paymentUrl || undefined,
          additionalInfo: formData.additionalInfo || undefined,
          isActive: formData.isActive,
          displayOrder: formData.displayOrder,
        };
        await createPaymentAccount(createData);
      }
      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const renderFieldsByType = () => {
    switch (formData.accountType) {
      case PaymentAccountType.Instapay:
        return (
          <>
            <Field label="Payment Address" required>
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  placeholder="@username"
                  value={formData.paymentAddress}
                  onChange={(e) => handleChange("paymentAddress", e.target.value)}
                />
              )}
            </Field>
            <Field label="Payment URL">
              {(id) => (
                <Input
                  id={id}
                  type="url"
                  placeholder="https://..."
                  value={formData.paymentUrl}
                  onChange={(e) => handleChange("paymentUrl", e.target.value)}
                />
              )}
            </Field>
          </>
        );

      case PaymentAccountType.IBAN:
        return (
          <>
            <Field label="IBAN" required>
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  placeholder="EG250002054705470001000008825"
                  value={formData.iban}
                  onChange={(e) => handleChange("iban", e.target.value)}
                />
              )}
            </Field>
            <Field label="Bank Name" required>
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  placeholder="Bank Name"
                  value={formData.bankName}
                  onChange={(e) => handleChange("bankName", e.target.value)}
                />
              )}
            </Field>
            <Field label="BIC/SWIFT Code">
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  placeholder="BMISEGCXXXX"
                  value={formData.bic}
                  onChange={(e) => handleChange("bic", e.target.value)}
                />
              )}
            </Field>
          </>
        );

      case PaymentAccountType.BankTransfer:
        return (
          <>
            <Field label="Account Number" required>
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  placeholder="Account Number"
                  value={formData.accountNumber}
                  onChange={(e) => handleChange("accountNumber", e.target.value)}
                />
              )}
            </Field>
            <Field label="Bank Name" required>
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  placeholder="Bank Name"
                  value={formData.bankName}
                  onChange={(e) => handleChange("bankName", e.target.value)}
                />
              )}
            </Field>
            <Field label="Routing Number">
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  placeholder="Routing Number"
                  value={formData.routingNumber}
                  onChange={(e) => handleChange("routingNumber", e.target.value)}
                />
              )}
            </Field>
          </>
        );

      case PaymentAccountType.PayPal:
        return (
          <Field label="PayPal Email" required>
            {(id) => (
              <Input
                id={id}
                type="email"
                placeholder="email@example.com"
                value={formData.paymentAddress}
                onChange={(e) => handleChange("paymentAddress", e.target.value)}
              />
            )}
          </Field>
        );

      case PaymentAccountType.Satispay:
      case PaymentAccountType.Stripe:
        return (
          <Field label="Payment URL" required>
            {(id) => (
              <Input
                id={id}
                type="url"
                placeholder="https://..."
                value={formData.paymentUrl}
                onChange={(e) => handleChange("paymentUrl", e.target.value)}
              />
            )}
          </Field>
        );

      default:
        return (
          <Field label="Payment Details">
            {(id) => (
              <Textarea
                id={id}
                placeholder="Enter payment details..."
                value={formData.additionalInfo}
                onChange={(e) => handleChange("additionalInfo", e.target.value)}
                rows={3}
              />
            )}
          </Field>
        );
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      placement="side"
      size="sm"
      title={props.account ? "Edit Payment Account" : "Add Payment Account"}
      bodyClassName="payment-account-dialog__body"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {props.account ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

      <Field label="Account Name" required>
        {(id) => (
          <Input
            id={id}
            type="text"
            placeholder="e.g., Instapay Egypt"
            value={formData.accountName}
            onChange={(e) => handleChange("accountName", e.target.value)}
          />
        )}
      </Field>

      <Field label="Account Type" required>
        {(id) => (
          <Select
            id={id}
            block
            value={formData.accountType}
            onChange={(e) => handleChange("accountType", Number(e.target.value))}
          >
            {Object.entries(PaymentAccountTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {renderFieldsByType()}

      <Field label="Currency">
        {(id) => (
          <Input
            id={id}
            type="text"
            placeholder="e.g., EGP, USD, EUR"
            value={formData.currency}
            onChange={(e) => handleChange("currency", e.target.value)}
          />
        )}
      </Field>

      <Field label="Additional Information">
        {(id) => (
          <Textarea
            id={id}
            placeholder="Any additional instructions for payment..."
            value={formData.additionalInfo}
            onChange={(e) => handleChange("additionalInfo", e.target.value)}
            rows={3}
          />
        )}
      </Field>

      <FormGrid columns={2} className="payment-account-dialog__row">
        <Field label="Display Order">
          {(id) => (
            <Input
              id={id}
              type="number"
              min="0"
              value={formData.displayOrder}
              onChange={(e) => handleChange("displayOrder", Number(e.target.value))}
            />
          )}
        </Field>

        <Switch
          className="payment-account-dialog__active"
          checked={formData.isActive}
          onChange={(checked) => handleChange("isActive", checked)}
          label="Active"
        />
      </FormGrid>
    </Dialog>
  );
};

export default PaymentAccountDialog;
